import PDFDocument, * as PDFKit from 'pdfkit';
import BlobStream from 'blob-stream';
import { CalculationState } from '@/lib/calculationstate';
import { format as formatCurrency } from '@/lib/currency';
import { BarGraph } from '@/lib/graphs';
import {
  col3,
  col4,
  COLOR_FONT,
  CONTENT_WIDTH,
  FONT,
  FONT_BOLD,
  hline,
  PAGE_MARGINS,
  SIZE_DESCRIPTION,
  SIZE_KEY,
} from '@/lib/pdf/helpers';
import drawGraphs from '@/lib/pdf/graphs';
import table from '@/lib/pdf/table';

// Disable param reassign because working with doc becomes horrific otherwise
/* eslint no-param-reassign: 0 */

/**
 * Draws the page header for each page of the pdf
 * Also draws the table header if hasTable is true
 *
 * @param doc
 * @param hasTable
 */
function header(doc: PDFKit.PDFDocument, hasTable: boolean) {
  // NV schade logo
  doc
    .save()
    .translate(440, 0)
    .scale(0.13, 0.13)
    // eslint-disable-next-line
    .path('M840.538,339.996C837.68,328.996 828.742,322.48 817.48,322.48C806.753,322.48 798.171,328.996 794.772,339.996L840.538,339.996ZM749.901,350.184C749.901,335.522 739.354,323.725 726.303,323.725C713.253,323.725 702.705,335.165 702.705,350.184C702.705,364.842 713.253,376.104 726.303,376.104C739.354,376.104 749.901,364.483 749.901,350.184M660.809,351.611C655.809,354.83 647.958,356.439 638.304,357.511C626.684,358.764 619.457,361.265 619.457,368.06C619.457,373.242 625.652,376.822 635.127,376.822C649.246,376.822 660.809,371.992 660.809,360.194L660.809,351.611ZM516.809,364.996L516.809,334.996L496.194,334.996C492.975,328.996 485.644,323.284 477.061,323.284C464.01,323.284 453.464,335.391 453.464,350.23C453.464,364.711 464.01,376.741 477.061,376.741C485.824,376.741 493.332,370.996 496.372,364.996L516.809,364.996ZM429.552,347.928C429.778,341.632 431.117,335.996 433.405,329.996L411.452,329.996C410.022,325.996 404.837,322.481 396.077,322.481C385.173,322.481 380.703,326.5 380.703,329.896C380.703,334.723 387.496,336.286 398.937,338.075C411.745,340.176 422.531,342.629 429.552,347.928M863.961,358.996L794.417,358.996C797.459,369.996 806.395,377.477 817.48,377.477C825.344,377.477 833.034,373.996 836.966,366.996L862.175,366.996C856.808,384.996 840.693,398.275 817.813,398.275C797.71,398.275 779.809,385.189 773.809,366.977L773.809,395.996L750.809,395.996L750.809,386.116C743.809,393.624 734.072,397.916 722.81,397.916C704.911,397.916 688.809,386.334 682.809,369.142L682.809,380.931C682.809,385.4 683.913,391.996 685.163,395.996L663.533,395.996C662.461,393.996 661.746,390.79 661.21,387.393C655.13,393.827 645.882,397.745 633.012,397.745C619.168,397.745 605.809,392.766 599.809,382.716L599.809,395.996L575.809,395.996L575.809,342.136C575.809,336.773 574.043,324.619 558.666,324.619C548.121,324.619 540.809,331.768 540.809,342.136L540.809,395.996L516.809,395.996L516.809,375.852C509.81,390.016 494.902,398.092 476.818,398.092C459.132,398.092 444.783,389.348 436.621,376.098C431.992,389.359 417.237,398.133 397.805,398.133C374.387,398.133 357.105,385.996 354.245,365.996L378.201,365.996C381.418,373.996 390.893,376.682 397.866,376.682C407.341,376.682 414.492,373.801 414.492,368.436C414.492,363.254 411.274,361.097 395.184,358.594C378.737,356.093 356.749,351.616 356.749,331.06C356.749,316.582 369.441,301.565 396.077,301.565C415.667,301.565 430.615,311.437 435.083,326.73C442.847,311.764 457.87,301.563 476.818,301.563C494.896,301.563 509.81,309.883 516.809,324.194L516.809,262.996L540.809,262.996L540.809,308.711C546.809,304.244 554.038,301.562 562.085,301.562C584.608,301.562 599.809,317.827 599.809,342.136L599.809,353.331C605.809,344.318 618.96,341.257 633.369,339.994C648.743,338.563 659.627,336.773 659.627,329.266C659.627,324.441 653.354,320.442 642.807,320.442C634.406,320.442 624.739,323.996 622.595,331.996L598.998,331.996C601.322,314.996 618.123,301.497 642.259,301.497C676.624,301.497 681.798,321.932 682.409,330.911C689.113,313.547 704.72,301.903 722.684,301.903C733.411,301.903 742.809,305.851 749.809,312.643L749.809,262.996L773.809,262.996L773.809,332.985C779.809,314.678 797.71,301.499 817.813,301.499C844.985,301.499 867.36,322.996 863.961,358.996')
    .fillColor([112, 174, 232])
    .fill()
    // eslint-disable-next-line
    .path('M299.181,299.996L273.796,299.996L251.612,241.204C251.74,242.758 251.809,244.135 251.809,245.763L251.809,299.996L227.809,299.996L227.809,245.763C227.809,240.401 226.22,227.533 210.668,227.533C200.12,227.533 192.809,235.397 192.809,245.763L192.809,299.996L169.809,299.996L169.809,207.996L191.809,207.996L191.809,216.451C197.809,209.479 205.899,205.19 215.731,205.19C226.906,205.19 236.187,209.78 242.487,217.327L238.935,207.996L263.606,207.996L286.489,276.635L309.551,207.996L333.863,207.996L299.181,299.996ZM248.167,135.142C185.745,135.142 135.142,185.745 135.142,248.166C135.142,310.588 185.745,361.19 248.167,361.19C310.588,361.19 361.192,310.588 361.192,248.166C361.192,185.745 310.588,135.142 248.167,135.142')
    .fillColor([154, 169, 41])
    .fill()
    .restore();

  doc.x = PAGE_MARGINS;
  doc.y = PAGE_MARGINS;

  if (hasTable) {
    doc.save();
    doc.lineWidth(2);
    hline(doc);
    doc
      .fontSize(SIZE_KEY)
      .moveDown();

    const { y } = doc;
    doc
      .font(FONT_BOLD)
      .fontSize(SIZE_KEY)
      .fillColor(COLOR_FONT)
      .text('Periode', col3(0), y)
      .text('Bruto jaarinkomen', col3(1), y)
      .text('Opbouw inkomen', col3(2), y);

    doc
      .fontSize(6)
      .moveDown()
      .lineWidth(1);
    hline(doc);
    doc
      .moveDown()
      .moveDown();

    doc
      .restore()
      // ''Incorrect'' reset of the font to proper values
      // Because I can't see a way to get the original values
      .font(FONT)
      .fontSize(SIZE_DESCRIPTION);
  }
}

/**
 * Draws the small table at the beginning containing the few calculation params
 *
 * After calling this function the cursor is positioned underneath at the left margin.
 *
 * @param doc PDF document in progress
 * @param state Calculation state to use
 */
function calculationStateInfo(doc: PDFKit.PDFDocument, state: CalculationState) {
  hline(doc);
  doc.moveDown();

  const disabilityPercent = Math.round(state.disabilityRatio * 100);
  let { y } = doc;
  doc
    .fontSize(SIZE_DESCRIPTION)
    .font(FONT)
    .text('Leeftijd', col4(0), y)
    .font(FONT_BOLD)
    .text(`${state.age}`, col4(1), y)
    .font(FONT)
    .text('Arbeidsongeschiktheid', col4(2), y)
    .font(FONT_BOLD)
    .text(`${disabilityPercent}%`, col4(3), y)
    .moveDown();

  y = doc.y;
  doc
    .font(FONT)
    .text('Bruto jaarsalaris', col4(0), y)
    .font(FONT_BOLD)
    .text(formatCurrency(state.salary), col4(1), y)
    .font(FONT)
    .text('Bruto bijverdiensten', col4(2), y)
    .font(FONT_BOLD)
    .text(formatCurrency(state.extraIncome), col4(3), y)
    .fontSize(7)
    .moveDown()
    .fontSize(SIZE_DESCRIPTION);

  hline(doc);
  doc
    .moveDown()
    .moveTo(PAGE_MARGINS, doc.y);
}

/**
 * Generates a pdf based on the given calculation state and graphs.
 *
 * Expects the graphs to be yearly calculated graphs, otherwise things go really weird
 *
 * @param state State to use
 * @param graphs Yearly graphs to use
 * @return The PDF blob
 */
export default async function createPdf(
  state: CalculationState,
  graphs: BarGraph[],
): Promise<Blob> {
  // Can't use async-await yet due to return from callback in stream finish
  return new Promise((resolve) => {
    const doc = new PDFDocument({
      autoFirstPage: false,
      size: 'A4',
    });
    const stream = doc.pipe(BlobStream());

    doc.font('Helvetica');

    let hasTable = false;
    doc.on('pageAdded', () => header(doc, hasTable));

    doc.addPage();
    doc.fontSize(18).text('Uw inkomen bij arbeidsongeschiktheid', {
      width: CONTENT_WIDTH,
      align: 'center',
    });

    doc.fontSize(SIZE_KEY).moveDown();
    calculationStateInfo(doc, state);
    drawGraphs(doc, state, graphs);

    hasTable = true;
    doc.addPage();

    graphs.forEach((g) => table(doc, state, g));

    doc.end();
    stream.on('finish', () => {
      resolve(stream.toBlob('application/pdf'));
    });
  });
}
